/**
 * ข้อความ/ลิงก์เชิญพนักงานผูกบัญชี LINE — ที่เดียวสำหรับทุกหน้าที่ต้องส่งคำเชิญ
 * (เพิ่มพนักงาน · ติดตามรับพนักงานใหม่) ไม่งั้นแก้ข้อความที่หนึ่งแล้วอีกที่ค้างของเก่า
 *
 * 2 เส้นทาง:
 *  - claim  = พนักงานยืนยันตัวเองด้วยเลขบัตรประชาชน (ใช้ได้เมื่อมีเลขบัตรในระบบแล้ว)
 *  - pair   = ระบบออก "รหัสจับคู่" สุ่มมีวันหมดอายุ ให้ HR ส่งให้พนักงาน (ใช้ได้ทุกคน)
 */
import { LIFF_ID } from './liff';

/** ลิงก์ LIFF หน้าผูกบัญชีพนักงาน (จับคู่ด้วยรหัส). */
export const pairLink = LIFF_ID ? `https://liff.line.me/${LIFF_ID}/pair` : '';

/** ลิงก์ LIFF หน้า claim (ยืนยันด้วยเลขบัตร) สำหรับ tenant นี้. */
export const claimLinkFor = (code: string) =>
  code && LIFF_ID ? `https://liff.line.me/${LIFF_ID}/claim?tenant=${code}` : '';

/** ข้อความเชิญพร้อมส่ง: ลิงก์ + 2 รหัส + ขั้นตอน. */
export function buildPairMessage(a: { name: string; tenantCode: string; pairingCode: string; expiresAt: string }): string {
  const link = pairLink || 'เปิดแอป PayPilot ในเมนู LINE ของบริษัท';
  const expires = new Date(a.expiresAt).toLocaleString('th-TH');
  return `สวัสดีคุณ ${a.name} 🎉

คุณได้รับเชิญเข้าใช้ระบบเงินเดือน (PayPilot)

📱 วิธีเชื่อมบัญชี:
1) เปิดลิงก์นี้ในแอป LINE
   ${link}
2) เลือก "ฉันเป็นพนักงาน"
3) กรอก 2 รหัสนี้:
   🏢 รหัสบริษัท:  ${a.tenantCode}
   🔑 รหัสจับคู่:  ${a.pairingCode}
4) ถ่ายเซลฟี่ยืนยันตัวตน แล้วกรอกข้อมูลส่วนตัว

⏰ รหัสหมดอายุ: ${expires}`;
}

/** ข้อความเชิญ (claim path) พร้อมส่ง: ลิงก์ + ขั้นตอน. */
export function buildClaimMessage(a: { name: string; tenantCode: string }): string {
  const link = claimLinkFor(a.tenantCode) || 'เปิดแอป PayPilot ในเมนู LINE ของบริษัท';
  return `สวัสดีคุณ ${a.name} 🎉

คุณได้รับเชิญเข้าใช้ระบบเงินเดือน (PayPilot)

📱 วิธีเชื่อมบัญชี:
1) เปิดลิงก์นี้ในแอป LINE
   ${link}
2) กรอกเลขบัตรประชาชนของคุณ
3) ยืนยันชื่อที่ระบบแสดง
4) เชื่อมบัญชี แล้วกรอกข้อมูลส่วนตัว`;
}

/**
 * ส่งผ่าน LINE ถ้าเปิดในแอปได้ ไม่ได้ก็คัดลอกให้ — คืนว่าไปทางไหน
 * (คนใช้อยู่บนเบราว์เซอร์ desktop จะไม่มี shareTargetPicker)
 */
export async function shareOrCopyInvite(msg: string): Promise<'shared' | 'copied'> {
  const liff = (window as unknown as {
    liff?: { isApiAvailable: (s: string) => boolean; shareTargetPicker: (m: unknown[]) => Promise<unknown> };
  }).liff;
  if (liff?.isApiAvailable('shareTargetPicker')) {
    try {
      await liff.shareTargetPicker([{ type: 'text', text: msg }]);
      return 'shared';
    } catch { /* ส่งไม่ได้ → ถอยไปคัดลอก */ }
  }
  await navigator.clipboard?.writeText(msg);
  return 'copied';
}
